import { useMemo } from 'react';

import { routes } from '../../../assets/constants';
import { ApiMethod } from '../../../common/models/api-types.type';
import { useAuthContext } from '../../../common/hooks/use-auth-context.hook';
import { useContentStudioApi } from '../../../common/hooks/use-content-studio-api.hook';
import { ReferenceAnalysis, StudioBoard, StudioShot, resolveEngine, keyPhotoUrl, isTakeShot, shotLookUrl } from './studio-contract';

export interface StudioPrices {
    still: number;
    clip: { fast: Record<string, number>; cinematic: Record<string, number> };
    /** A 3D take made real, by its length in seconds. */
    take?: Record<string, number>;
}

/** The price of making a take of this length real, from the API's table. */
export function takePrice(prices: StudioPrices | null, seconds: number): number | null {
    const table = prices?.take;
    if (!table) return null;
    const keys = Object.keys(table).map(Number).sort((a, b) => a - b);
    const at = keys.find((k) => k >= Math.ceil(seconds)) ?? keys[keys.length - 1];
    return table[String(at)] ?? null;
}

/** What filming this shot costs, whichever way it is filmed. */
export function shotClipPrice(prices: StudioPrices | null, shot: StudioShot): number | null {
    if (isTakeShot(shot)) return takePrice(prices, shot.take3d!.durationSec);
    return clipPrice(prices, resolveEngine(shot), generatedClipSeconds(shot));
}

/** The clip price for a duration, from the table the API publishes (4/5/6/8/10 s). */
export function clipPrice(prices: StudioPrices | null, engine: 'fast' | 'cinematic', seconds: number): number | null {
    if (!prices) return null;
    const table = prices.clip[engine] || {};
    const keys = Object.keys(table).map(Number).sort((a, b) => a - b);
    if (!keys.length) return null;
    const n = Math.max(4, Math.min(10, Math.round(seconds || 5)));
    const at = keys.find((k) => k >= n) ?? keys[keys.length - 1];
    return table[String(at)];
}

/** Clip lengths are generated at 4 s minimum and trimmed in the edit. */
export function generatedClipSeconds(shot: StudioShot): number {
    return Math.max(4, Math.min(10, Math.round(shot.durationSec || 5)));
}

/**
 * The Studio's API: the Content Studio endpoints it shares, plus the two of
 * its own (reference breakdown, price list), plus shot generation in the
 * shape the worker's studio pipeline expects.
 */
export function useStudioApi() {
    const cs = useContentStudioApi();
    const { sendAuthGuardedRequest } = useAuthContext() as any;

    return useMemo(() => {
        const analyzeReference = async (input: {
            url: string; kind: 'video' | 'image'; cuts?: number[]; frames?: Array<{ t: number; url: string }>; durationSec?: number;
        }): Promise<ReferenceAnalysis> => {
            const res: any = await sendAuthGuardedRequest(ApiMethod.POST, routes.contentStudio.studioAnalyzeReference, input);
            return res?.data || res;
        };

        const getPrices = async (): Promise<StudioPrices> => {
            const res: any = await sendAuthGuardedRequest(ApiMethod.GET, routes.contentStudio.studioPricing);
            return res?.data || res;
        };

        /** Start the still for a shot. Returns the pending asset's uuid. */
        const makeStill = async (projectUuid: string, board: StudioBoard, shot: StudioShot): Promise<string> => {
            const res = await cs.createGeneration({
                projectUuid,
                recipeSlug: 'studio-still',
                sourceProductUuids: shot.productUuids,
                inputs: {
                    look: board.look,
                    format: board.format,
                    scene: shot.scene,
                    camera: shot.camera,
                    cameraNote: shot.cameraNote,
                    framingRef: shot.framingRef,
                    action: shot.action,
                    kind: shot.kind,
                    shotId: shot.id,
                    // Made from its set's key photo: re-photographed, not reinvented.
                    // (Not a tile shot as an object: its 3D capture is its truth.)
                    baseStillUrl: shot.surfaceUse === 'object' ? undefined : keyPhotoUrl(board, shot),
                    surfaceUse: shot.surfaceUse,
                    // Where the real tile is laid; unset, the worker reads the scene.
                    surface: shot.surfaceUse === 'object' ? undefined : shot.surfaceOn,
                    // Light only: the photo takes its lighting and grade, never its framing.
                    lookUrl: shot.surfaceUse === 'object' ? undefined : shotLookUrl(board, shot),
                    pose: shot.pose,
                },
                count: 1,
            } as any);
            const asset = (res as any)?.assets?.[0];
            if (!asset?.uuid) throw new Error('The still could not be started');
            return asset.uuid;
        };

        /** Start the clip for a shot: from its approved still, or from its own 3D take made real. */
        const makeClip = async (projectUuid: string, board: StudioBoard, shot: StudioShot): Promise<string> => {
            if (isTakeShot(shot)) {
                const take = shot.take3d!;
                const stillReady = shot.still?.status === 'ready' && !!shot.still.url;
                const res = await cs.createGeneration({
                    projectUuid,
                    recipeSlug: 'studio-clip',
                    sourceProductUuids: shot.productUuids,
                    ...(stillReady && shot.still?.assetUuid ? { parentAssetUuid: shot.still.assetUuid } : {}),
                    inputs: {
                        engine: 'take',
                        takeUrl: take.url,
                        takePosterUrl: take.posterUrl,
                        // The measured length: billed by the second, rounded up.
                        durationSec: take.durationSec,
                        stillUrl: stillReady ? shot.still!.url : undefined,
                        // The take's first frame is lit like this, else like the shot's photo.
                        lookUrl: shotLookUrl(board, shot),
                        look: board.look,
                        format: board.format,
                        scene: shot.scene,
                        action: shot.action,
                        shotId: shot.id,
                    },
                    count: 1,
                } as any);
                const asset = (res as any)?.assets?.[0];
                if (!asset?.uuid) throw new Error('The clip could not be started');
                return asset.uuid;
            }
            if (!shot.still?.assetUuid || shot.still.status !== 'ready') throw new Error('Make the photo first');
            const res = await cs.createGeneration({
                projectUuid,
                recipeSlug: 'studio-clip',
                sourceProductUuids: shot.productUuids,
                parentAssetUuid: shot.still.assetUuid,
                inputs: {
                    look: board.look,
                    format: board.format,
                    scene: shot.scene,
                    action: shot.action,
                    motion: shot.motion || 'push-in',
                    durationSec: generatedClipSeconds(shot),
                    engine: resolveEngine(shot),
                    stillUrl: shot.still.url,
                    shotId: shot.id,
                },
                count: 1,
            } as any);
            const asset = (res as any)?.assets?.[0];
            if (!asset?.uuid) throw new Error('The clip could not be started');
            return asset.uuid;
        };

        return { ...cs, analyzeReference, getPrices, makeStill, makeClip };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sendAuthGuardedRequest]);
}
